import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus, ListPlus, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { FILL_LABELS, savedValues, addValue, removeValue, savedLists, removeList } from "../../lib/saved";
import { insertHTML } from "./cmd";
import { esc } from "../../lib/pages";

export const listHTML = (opts) => `<span class="field-wrap" contenteditable="false"><select class="dd-field">${opts.map((o, i) => `<option${i === 0 ? " selected" : ""}>${esc(o)}</option>`).join("")}</select></span>&nbsp;`;

export const SavedFieldsDialog = ({ open, onClose, regions }) => {
  const [vals, setVals] = useState({});
  const [lists, setLists] = useState([]);
  const [label, setLabel] = useState(FILL_LABELS[0]);
  const [value, setValue] = useState("");
  const reload = () => { setVals(savedValues()); setLists(savedLists()); };
  useEffect(() => { if (open) reload(); }, [open]);

  const add = (e) => { e.preventDefault(); if (addValue(label, value)) { setValue(""); reload(); } };
  const fromDoc = () => {
    let n = 0;
    regions().forEach((r) => r?.querySelectorAll(".fill-field").forEach((f) => { if (addValue(f.dataset.label || "Field", f.textContent)) n++; }));
    reload();
    toast(n ? `Saved ${n} value${n > 1 ? "s" : ""} from this document` : "No new filled-in values to save");
  };
  const insertList = (l) => { onClose(); setTimeout(() => insertHTML(listHTML(l.options)), 0); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[calc(100%-2rem)] rounded-lg sm:max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden grid-cols-[minmax(0,1fr)]" data-testid="saved-fields-dialog" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Saved fields</DialogTitle>
          <DialogDescription>Save details once, then click any fill-in field and pick a value from the drop-down. Saved on this device.</DialogDescription>
        </DialogHeader>
        <section className="space-y-2">
          <div className="text-sm font-medium">Saved values</div>
          <form onSubmit={add} className="flex flex-wrap gap-2">
            <select value={label} onChange={(e) => setLabel(e.target.value)} className="h-9 rounded-md border bg-background px-2 text-sm" data-testid="saved-value-label-select">
              {[...new Set([...FILL_LABELS, ...Object.keys(vals)])].map((l) => <option key={l}>{l}</option>)}
            </select>
            <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Value, e.g. Acme Pte Ltd" className="flex-1 min-w-[160px]" data-testid="saved-value-input" />
            <Button type="submit" size="sm" className="h-9" disabled={!value.trim()} data-testid="saved-value-add-btn"><Plus className="h-4 w-4" /></Button>
          </form>
          <Button size="sm" variant="outline" onClick={fromDoc} data-testid="saved-values-from-doc-btn"><Download className="h-4 w-4 mr-1.5" />Save filled fields from this document</Button>
          <div className="space-y-2 pt-1" data-testid="saved-values-list">
            {Object.entries(vals).map(([l, vs]) => (
              <div key={l}>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className="flex flex-wrap gap-1.5 mt-1">{vs.map((v) => (
                  <span key={v} className="inline-flex max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs"><span className="truncate">{v}</span>
                    <button type="button" onClick={() => { removeValue(l, v); reload(); }} aria-label={`Remove ${v}`} data-testid="saved-value-remove"><Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" /></button></span>
                ))}</div>
              </div>
            ))}
            {!Object.keys(vals).length && <p className="text-xs text-muted-foreground">Nothing saved yet.</p>}
          </div>
        </section>
        <section className="space-y-2 border-t pt-4">
          <div className="text-sm font-medium">Saved drop-down lists</div>
          {lists.map((l) => (
            <div key={l.name} className="flex flex-wrap items-center gap-2 text-sm" data-testid={`saved-list-${l.name}`}>
              <span className="font-medium truncate max-w-full">{l.name}</span><span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{l.options.join(" · ")}</span>
              <Button size="sm" variant="outline" className="h-7" onClick={() => insertList(l)} data-testid={`saved-list-insert-${l.name}`}><ListPlus className="h-3.5 w-3.5 mr-1" />Insert</Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => { removeList(l.name); reload(); }} data-testid={`saved-list-delete-${l.name}`}><Trash2 className="h-3.5 w-3.5" /></Button>
            </div>
          ))}
          {!lists.length && <p className="text-xs text-muted-foreground">Create one with Insert › Drop-down and tick “Save this list”.</p>}
        </section>
      </DialogContent>
    </Dialog>
  );
};
